import axios from 'axios';
import { clearCustomerPortalSession } from '../utils/customerPortalStorage';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const apiClient = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

const getStoredToken = () => localStorage.getItem('customer_token');
const getStoredOrgId = () => localStorage.getItem('organization_public_id');

/* ------------------------------------------------------------------ */
/*  Request interceptor — attach Bearer + Org header on every request  */
/* ------------------------------------------------------------------ */
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  const orgId = getStoredOrgId();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  if (orgId) config.headers['X-Organization-Public-Id'] = orgId;
  return config;
});

/* ------------------------------------------------------------------ */
/*  Response interceptor — clear session on 401                        */
/* ------------------------------------------------------------------ */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const orgId = getStoredOrgId();
      clearCustomerPortalSession();
      const path = window.location.pathname;
      const onLoginPage = /\/[^/]+\/login$/.test(path) || path === '/login' || path.endsWith('/login');
      if (!onLoginPage) {
        window.location.href = orgId ? `/${encodeURIComponent(orgId)}/login` : '/login';
      }
    }
    return Promise.reject(error);
  }
);

/* ------------------------------------------------------------------ */
/*  Auth endpoints                                                      */
/* ------------------------------------------------------------------ */
const authService = {
  login: async (email, password, organizationPublicId) => {
    const response = await apiClient.post('/customer/login', {
      email,
      password,
      organization_public_id: organizationPublicId,
    });
    return response.data;
  },

  /** Public: org name for scoped login page (no auth). */
  getOrganizationBasicInfo: async (organizationPublicId) => {
    const response = await apiClient.get(
      `/organizations/${encodeURIComponent(organizationPublicId)}/info`,
    );
    const body = response.data;
    return body?.data ?? body;
  },

  verifyToken: async () => {
    const response = await apiClient.get('/customer/me');
    return response.data;
  },

  logout: async () => {
    try {
      await apiClient.post('/customer/logout');
    } catch {
      // Always proceed with local cleanup
    }
  },

  getProfile: async () => {
    const response = await apiClient.get('/customer/profile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await apiClient.put('/customer/profile', data);
    return response.data;
  },
};

export { apiClient };
export default authService;
